import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import {
  FlaskConical, Play, Loader2, RefreshCw, ShieldCheck, ShieldX,
  ChevronDown, Search, CheckCircle2, XCircle, MinusCircle, CircleDashed,
} from "lucide-react";

/** Quant Engine v2 — shadow-mode monitor (admin only).
 *  Per-symbol layer verdicts w/ detailed plain-English interpretation +
 *  on-demand 10-layer analysis of ANY Indian F&O symbol via search. */
const POLL_MS = 60_000;

const REGIME_COLOR = {
  trending: "text-[#22C55E] border-[#22C55E]/40",
  ranging: "text-[#F59E0B] border-[#F59E0B]/40",
  volatile: "text-[#EF4444] border-[#EF4444]/40",
  compression: "text-[#A855F7] border-[#A855F7]/40",
  low_volatility: "text-zinc-400 border-zinc-700",
  unknown: "text-zinc-500 border-zinc-800",
  no_data: "text-zinc-600 border-zinc-800",
};

const LAYER_STATUS = {
  pass: { icon: CheckCircle2, cls: "text-[#22C55E]" },
  fail: { icon: XCircle, cls: "text-[#EF4444]" },
  neutral: { icon: MinusCircle, cls: "text-zinc-400" },
  not_reached: { icon: CircleDashed, cls: "text-zinc-600" },
};

function StatCell({ label, value, accent }) {
  return (
    <div className="border border-zinc-800 bg-[#0a0a0a] p-2">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className={`font-heading font-black text-lg tracking-tight ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

export function InterpretationCard({ interp, testid }) {
  if (!interp) return null;
  return (
    <div className="mt-2 border-t border-zinc-900 pt-2 space-y-1.5" data-testid={testid}>
      <div className={`text-[11px] font-mono font-bold uppercase tracking-wider ${interp.headline?.startsWith("✔") ? "text-[#22C55E]" : "text-[#F59E0B]"}`}>
        {interp.headline}
      </div>
      {(interp.layers || []).map((l) => {
        const meta = LAYER_STATUS[l.status] || LAYER_STATUS.not_reached;
        const Icon = meta.icon;
        return (
          <div key={l.layer} className={`flex gap-2 items-start ${l.status === "not_reached" ? "opacity-40" : ""}`}>
            <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${meta.cls}`} />
            <div className="min-w-0">
              <span className={`text-[10px] font-mono font-bold uppercase tracking-widest ${meta.cls}`}>{l.layer}</span>
              <div className="text-[11px] text-zinc-400 leading-relaxed">{l.text}</div>
            </div>
          </div>
        );
      })}
      <div className={`text-[11px] leading-relaxed font-bold border-l-2 pl-2 ${interp.action?.startsWith("TRADE") ? "border-[#22C55E] text-[#22C55E]" : "border-[#F59E0B] text-[#F59E0B]"}`}>
        {interp.action}
      </div>
    </div>
  );
}

function QuantSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/options-lab/search?q=${encodeURIComponent(q.trim())}&limit=8`);
        setResults(data?.results || []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const run = async (sym) => {
    setAnalyzing(true); setAnalysis(null); setError(""); setResults([]); setQ(sym);
    try {
      const { data } = await api.get(`/admin/quant/analyze?symbol=${encodeURIComponent(sym)}`);
      setAnalysis(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Analysis failed");
    } finally { setAnalyzing(false); }
  };

  return (
    <div className="border border-zinc-800 bg-[#0a0a0a] p-3 mb-3" data-testid="quant-search-box">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">
        ◢ Analyze ANY Indian F&O symbol — indices & stocks — through all 10 layers
      </div>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-600" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) run(q.trim().toUpperCase()); }}
          placeholder="Search NIFTY, RELIANCE, TCS, HDFCBANK…"
          className="w-full bg-[#0e0e0e] border border-zinc-800 focus:border-[#A855F7] pl-8 pr-3 py-2 text-xs font-mono outline-none uppercase"
          data-testid="quant-search-input"
        />
        {results.length > 0 && (
          <div className="absolute z-20 mt-1 w-full bg-[#0e0e0e] border border-zinc-800 max-h-56 overflow-auto" data-testid="quant-search-results">
            {results.map((r) => (
              <button key={r.symbol} onClick={() => run(r.symbol)}
                      className="w-full text-left px-3 py-1.5 hover:bg-[#181818] text-xs font-mono flex justify-between"
                      data-testid={`quant-search-pick-${r.symbol}`}>
                <span className="text-white font-bold">{r.symbol}</span>
                <span className="text-zinc-500 truncate ml-2">{r.name} · {r.category}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {analyzing && (
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 py-3 text-center">
          <Loader2 className="h-3 w-3 animate-spin inline mr-2" />Running 10-layer institutional analysis…
        </div>
      )}
      {error && <div className="text-[11px] text-[#EF4444] mt-2 font-mono" data-testid="quant-search-error">✕ {error}</div>}
      {analysis && (
        <div className="mt-2">
          <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
            <span className="text-white font-bold text-xs">{analysis.symbol}</span>
            <span className="text-zinc-500">{analysis.name}</span>
            {analysis.regime?.regime && (
              <span className={`px-1.5 py-0.5 border ${REGIME_COLOR[analysis.regime.regime] || REGIME_COLOR.unknown}`}>{analysis.regime.regime}</span>
            )}
            {typeof analysis.probability === "number" && (
              <span className={analysis.probability >= 75 ? "text-[#22C55E] font-bold" : "text-zinc-400"}>{analysis.probability}%</span>
            )}
          </div>
          <InterpretationCard interp={analysis.interpretation} testid="quant-search-interpretation" />
        </div>
      )}
    </div>
  );
}

export default function QuantShadowPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null); // symbol whose interpretation is open

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/quant/status");
      setData(d);
    } catch {} finally { setLoading(false); }
  }, []);

  const scanNow = async () => {
    setScanning(true);
    try {
      await api.post("/admin/quant/scan-now?force=true");
      await load();
    } catch {} finally { setScanning(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const s = data?.shadow_stats || {};
  const li = data?.live_stats_indices || {};

  return (
    <div className="bg-[#121212] border border-zinc-800 p-4" data-testid="quant-shadow-panel">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setOpen((o) => !o)} className="text-left flex items-center gap-2 group" data-testid="quant-collapse-toggle">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#A855F7] font-mono flex items-center gap-1.5">
              <FlaskConical className="h-3 w-3" /> Quant Engine v2 · Shadow Mode
            </div>
            <div className="font-heading text-lg font-bold uppercase tracking-tight">10-Layer Institutional Engine</div>
          </div>
          <ChevronDown className={`h-4 w-4 text-zinc-500 group-hover:text-white transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <div className="flex items-center gap-2">
          <button onClick={scanNow} disabled={scanning} data-testid="quant-scan-now"
                  className="text-[10px] uppercase tracking-widest font-bold border border-zinc-700 hover:border-[#A855F7] px-2 py-1 inline-flex items-center gap-1.5">
            {scanning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Scan Now
          </button>
          <button onClick={load} className="text-zinc-500 hover:text-white p-1" data-testid="quant-refresh">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {open && (<>
      {/* On-demand analysis of any Indian symbol */}
      <QuantSearch />

      {/* Shadow vs Live comparison */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="quant-compare">
        <StatCell label="Shadow Win Rate" value={`${s.win_rate ?? 0}%`}
                  accent={(s.win_rate ?? 0) >= 50 ? "text-[#86efac]" : "text-[#fca5a5]"} />
        <StatCell label="Shadow W-L (open)" value={`${s.wins ?? 0}-${s.losses ?? 0} (${s.open ?? 0})`} />
        <StatCell label="Live Engine (3 idx)" value={`${li.win_rate ?? 0}%`}
                  accent={(li.win_rate ?? 0) >= 50 ? "text-[#86efac]" : "text-[#fca5a5]"} />
        <StatCell label="Live W-L (3 idx)" value={`${li.wins ?? 0}-${li.losses ?? 0}`} />
      </div>

      {/* Per-index latest verdict — click a row for the full layer-by-layer interpretation */}
      <div className="space-y-1.5 mb-3" data-testid="quant-scans">
        {(data?.scans || []).map((sc) => {
          const rg = sc.regime?.regime || sc.decision;
          const rgCls = REGIME_COLOR[rg] || REGIME_COLOR.unknown;
          const isTrade = sc.decision === "trade";
          const isOpen = expanded === sc.symbol;
          return (
            <div key={sc.symbol} className={`border-l-2 px-3 py-2 bg-[#0a0a0a] border ${isTrade ? "border-[#22C55E]/50" : "border-zinc-800"}`}
                 data-testid={`quant-scan-${sc.symbol}`}>
              <button onClick={() => setExpanded(isOpen ? null : sc.symbol)} className="w-full text-left" data-testid={`quant-expand-${sc.symbol}`}>
                <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
                  <span className="text-white font-bold text-xs">{sc.symbol}</span>
                  <span className={`px-1.5 py-0.5 border ${rgCls}`}>{rg}</span>
                  {sc.mtf && (
                    <span className="text-zinc-500">
                      D/{sc.mtf.daily > 0 ? "▲" : sc.mtf.daily < 0 ? "▼" : "—"} H/{sc.mtf.h1 > 0 ? "▲" : sc.mtf.h1 < 0 ? "▼" : "—"} 15/{sc.mtf.m15 > 0 ? "▲" : sc.mtf.m15 < 0 ? "▼" : "—"}
                    </span>
                  )}
                  {sc.wave?.label && <span className="text-[#A855F7]">{sc.wave.label}</span>}
                  {typeof sc.probability === "number" && (
                    <span className={sc.probability >= 75 ? "text-[#22C55E] font-bold" : "text-zinc-400"}>{sc.probability}%</span>
                  )}
                  {isTrade && <span className="text-[#22C55E] font-bold">◢ {sc.direction} SETUP</span>}
                  <ChevronDown className={`h-3 w-3 text-zinc-600 ml-auto transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
                {!isTrade && sc.reason && !isOpen && (
                  <div className="text-[10px] text-zinc-500 mt-1 truncate" title={sc.reason}>✕ {sc.reason} · click for full interpretation</div>
                )}
                {isTrade && sc.risk && (
                  <div className="text-[10px] font-mono text-zinc-400 mt-1">
                    E {sc.risk.entry} · SL {sc.risk.stop_loss} · T1 {sc.risk.target_1} · T2 {sc.risk.target_2} · T3 {sc.risk.target_3} · R:R 1:{sc.risk.rr_t2}
                  </div>
                )}
              </button>
              {isOpen && <InterpretationCard interp={sc.interpretation} testid={`quant-interp-${sc.symbol}`} />}
            </div>
          );
        })}
        {(data?.scans || []).length === 0 && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 py-3 text-center">
            No scans yet — engine runs every 5 min during market hours, or press Scan Now.
          </div>
        )}
      </div>

      {/* Recent shadow trades */}
      {(data?.recent_shadow_trades || []).length > 0 && (
        <div className="space-y-1 max-h-56 overflow-auto" data-testid="quant-trades">
          {data.recent_shadow_trades.map((t) => (
            <div key={t.signal_id} className="grid grid-cols-12 gap-2 items-center text-[10px] font-mono py-1 px-2 border-l-2 border-zinc-800 bg-[#0a0a0a]">
              <div className="col-span-2 text-zinc-300">{t.symbol}</div>
              <div className={`col-span-2 uppercase ${t.trend === "bullish" ? "text-[#86efac]" : "text-[#fca5a5]"}`}>{t.trend}</div>
              <div className="col-span-2 text-zinc-400">{t.initial_probability}% · {t.wave_label}</div>
              <div className={`col-span-3 uppercase font-bold ${t.status === "miss" ? "text-[#fca5a5]" : t.status?.startsWith("hit") ? "text-[#86efac]" : "text-zinc-500"}`}>
                {t.status}{t.close_reason === "time_budget" ? " · time" : ""}
              </div>
              <div className="col-span-2 flex items-center gap-1">
                {t.passed_llm_veto
                  ? <ShieldCheck className="h-3 w-3 text-[#22C55E]" title="LLM approved" />
                  : <ShieldX className="h-3 w-3 text-[#EF4444]" title={t.llm_veto?.flaw || "LLM vetoed"} />}
                <span className="text-zinc-600">{t.llm_veto?.status === "skipped" ? "no-veto" : "veto"}</span>
              </div>
              <div className="col-span-1 text-zinc-600 text-right">
                {new Date(t.generated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}
